import { Box, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { format } from 'date-fns';
import { DealerOnly, dashboardCardSx } from '../common';
import { useDashboardRange } from './DashboardRangeContext';
import { useRangeDealerStatistics } from './useRecentDealerStatistics';

const ACCEPTED_COLOR = '#1E8A3C';
const REJECTED_COLOR = '#C62327';

interface SummaryProps {
  label: string;
  value: string;
  isLoading?: boolean;
}

const Summary = ({ label, value, isLoading }: SummaryProps) => (
  <Stack spacing={0.5}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton variant="rounded" width={56} height={32} />
    ) : (
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    )}
  </Stack>
);

const AccidentsTrendWidgetConnectedContent = () => {
  const { days, counts, isLoading } = useRangeDealerStatistics();
  const { label } = useDashboardRange();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const tickStep = Math.max(1, Math.ceil(days.length / 6));

  const accepted = counts.reduce((sum, c) => sum + c.accepted, 0);
  const rejected = counts.reduce((sum, c) => sum + c.rejected, 0);
  const spotSolved = counts.reduce((sum, c) => sum + c.spotSolved, 0);
  const spotSolvedShare = accepted > 0 ? Math.round((spotSolved / accepted) * 100) : 0;

  return (
    <Box
      sx={{
        ...dashboardCardSx,
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: { xs: 2, md: 3 }
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Stack>
          <Typography variant="h6" fontWeight={600}>
            Nehody v období
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}, denní počet řešených a neřešených
          </Typography>
        </Stack>
        <Stack direction="row" gap={4}>
          <Summary label="Řešené" value={String(accepted)} isLoading={isLoading} />
          <Summary label="Neřešené" value={String(rejected)} isLoading={isLoading} />
          <Summary label="Vyřešeno na místě" value={`${spotSolvedShare} %`} isLoading={isLoading} />
        </Stack>
      </Stack>
      {isLoading ? (
        <Skeleton variant="rounded" height={260} />
      ) : (
        <BarChart
          height={260}
          skipAnimation={reduceMotion}
          margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
          borderRadius={4}
          series={[
            { data: counts.map((c) => c.accepted), label: 'Řešené', color: ACCEPTED_COLOR, stack: 'total' },
            { data: counts.map((c) => c.rejected), label: 'Neřešené', color: REJECTED_COLOR, stack: 'total' }
          ]}
          xAxis={[
            {
              scaleType: 'band',
              data: days,
              tickInterval: (_, index) => index % tickStep === 0,
              valueFormatter: (value: Date, context) =>
                context.location === 'tooltip' ? format(value, 'd. M. yyyy') : format(value, 'd. M.')
            }
          ]}
          yAxis={[{ tickMinStep: 1, disableLine: true, disableTicks: true }]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            '& .MuiChartsGrid-line': { stroke: 'rgba(34, 34, 52, 0.08)' },
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(34, 34, 52, 0.2)' }
          }}
        />
      )}
      <Stack direction="row" gap={3}>
        {[
          ['Řešené', ACCEPTED_COLOR],
          ['Neřešené', REJECTED_COLOR]
        ].map(([label, color]) => (
          <Stack key={label} direction="row" alignItems="center" gap={1}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: color }} />
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export const AccidentsTrendWidgetConnected = () => (
  <DealerOnly>
    <AccidentsTrendWidgetConnectedContent />
  </DealerOnly>
);
